import { Show, SimpleShowLayout, TextField, DateField, Title, useGetOne, useNotify, FunctionField, useUpdate, useGetIdentity } from 'react-admin';
import { Box, Typography, Chip, Skeleton, Stack, Avatar, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField, Alert, Link as MuiLink } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { canOpenPlatformUserShow, getPlatformUserShowUrl } from './platformUserShowLink';

const complaintTypeLabels: Record<string, string> = {
    'INCORRECT_PRICE': 'Некорректная цена',
    'INCORRECT_DESCRIPTION_OR_PHOTOS': 'Некорректное описание или фото',
    'NOT_ACTUAL_AD': 'Неактуальное объявление',
    'SCAM': 'Скам',
    'FAKE': 'Подделка вещи'
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'NEW':
            return 'error';
        case 'IN_PROGRESS':
            return 'warning';
        case 'REJECTED':
            return 'default';
        case 'APPLIED':
            return 'success';
        default:
            return 'default';
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case 'NEW':
            return 'Открыта';
        case 'IN_PROGRESS':
            return 'Рассматривается';
        case 'REJECTED':
            return 'Отклонена';
        case 'APPLIED':
            return 'Приняты меры';
        default:
            return status;
    }
};

const YOU_TRACK_ISSUE_PREFIX = 'https://youtrack.looton.ru/';
const YOU_TRACK_BOARD_URL = 'https://youtrack.looton.ru/projects/TRUST/agiles/';

const isValidYouTrackIssueUrl = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith(YOU_TRACK_ISSUE_PREFIX)) {
        return false;
    }

    try {
        const parsedUrl = new URL(trimmedValue);
        return parsedUrl.protocol === 'https:' && parsedUrl.hostname === 'youtrack.looton.ru';
    } catch (_error) {
        return false;
    }
};

export const ComplaintShow = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();
    const [update] = useUpdate();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenUserShow = canOpenPlatformUserShow(currentUser?.role);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [takeInWorkDialogOpen, setTakeInWorkDialogOpen] = useState(false);
    const [youTrackIssueUrl, setYouTrackIssueUrl] = useState('');
    const [comment, setComment] = useState('');

    const { data: complaint, isLoading, error } = useGetOne(
        'complaints',
        { id: id! }
    );

    const handleTakeInWork = async () => {
        const trimmedIssueUrl = youTrackIssueUrl.trim();
        if (!isValidYouTrackIssueUrl(trimmedIssueUrl)) {
            notify('Введите корректную ссылку вида https://youtrack.looton.ru/*', { type: 'warning' });
            return;
        }

        try {
            await update(
                'complaints',
                {
                    id: id!,
                    data: {
                        complaintStatusResult: 'IN_PROGRESS',
                        youTrackIssueUrl: trimmedIssueUrl
                    }
                }
            );
            notify('Жалоба у вас в работе', { type: 'success' });
            setTakeInWorkDialogOpen(false);
            setYouTrackIssueUrl('');
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } catch (error) {
            notify('Ошибка при обновлении статуса', { type: 'error' });
        }
    };

    const handleReject = async () => {
        try {
            await update(
                'complaints',
                {
                    id: id!,
                    data: {
                        complaintStatusResult: 'REJECTED',
                        comment: comment
                    }
                }
            );
            notify('Жалоба отклонена', { type: 'success' });
            setRejectDialogOpen(false);
            setComment('');
            navigate('/complaints');
        } catch (error) {
            notify('Ошибка при отклонении жалобы', { type: 'error' });
        }
    };

    const handleApprove = async () => {
        try {
            await update(
                'complaints',
                {
                    id: id!,
                    data: {
                        complaintStatusResult: 'APPLIED',
                        comment: comment
                    }
                }
            );
            notify('Жалоба одобрена', { type: 'success' });
            setApproveDialogOpen(false);
            setComment('');
            navigate('/complaints');
        } catch (error) {
            notify('Ошибка при одобрении жалобы', { type: 'error' });
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Детали жалобы" />
                <Stack spacing={2}>
                    <Skeleton variant="text" width={300} height={40} />
                    <Skeleton variant="rectangular" width="100%" height={200} />
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="text" width={150} />
                </Stack>
            </Box>
        );
    }

    if (error) {
        notify('Ошибка при загрузке данных жалобы', { type: 'error' });
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Title title="Ошибка" />
                <Typography variant="h6" color="error">
                    Не удалось загрузить данные жалобы
                </Typography>
            </Box>
        );
    }

    if (!complaint) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Title title="Жалоба не найдена" />
                <Typography variant="h6">
                    Жалоба с указанным ID не найдена
                </Typography>
            </Box>
        );
    }

        return (
        <>
            <Show title="Детали жалобы">
                <SimpleShowLayout>
                    <TextField source="id" label="ID жалобы" />
                    <TextField source="adId" label="ID объявления" />
                    <DateField source="createdDate" label="Дата создания" showTime />
                    <DateField source="lastUpdatedDate" label="Дата последнего обновления" showTime />
                    <FunctionField
                        label="Тип жалобы"
                        render={record => {
                            const type = record?.complaintType;
                            return complaintTypeLabels[type] || type;
                        }}
                    />
                    <FunctionField
                        label="Текущий статус"
                        render={record => {
                            const status = record?.currentComplaintStatus;
                            if (!status) return null;
                            return (
                                <Chip 
                                    label={getStatusLabel(status)} 
                                    color={getStatusColor(status) as any}
                                    size="medium"
                                />
                            );
                        }}
                    />
                    {complaint?.youTrackIssueUrl && (
                        <FunctionField
                            label="Задача YouTrack"
                            render={record => {
                                const issueUrl = record?.youTrackIssueUrl;
                                if (!issueUrl) return null;

                                return (
                                    <MuiLink
                                        href={issueUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                    >
                                        {issueUrl}
                                    </MuiLink>
                                );
                            }}
                        />
                    )}
                    <FunctionField
                        label="Сообщение от пользователя"
                        render={record => {
                            const message = record?.reportMessage;
                            if (!message) return 'Сообщение не указано';
                            return (
                                <Box sx={{ 
                                    p: 2, 
                                    bgcolor: 'background.paper', 
                                    borderRadius: 1, 
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Typography variant="body2">{message}</Typography>
                                </Box>
                            );
                        }}
                    />
                    <FunctionField
                        label="Пользователь, подавший жалобу"
                        render={record => {
                            const user = record?.reporterUser;
                            if (!user) return 'Информация о пользователе недоступна';
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar 
                                        src={user.avatarUrl || undefined} 
                                        alt={user.username}
                                        sx={{ width: 40, height: 40 }}
                                    >
                                        {user.username.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body1" fontWeight="medium">
                                            {user.username}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            ID: {user.userId ? (
                                                canOpenUserShow ? (
                                                    <MuiLink
                                                        href={getPlatformUserShowUrl(user.userId)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ fontSize: 'inherit' }}
                                                    >
                                                        {user.userId}
                                                    </MuiLink>
                                                ) : user.userId
                                            ) : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                    />
                    {complaint?.result && (
                        <FunctionField
                            label="Результат модерации"
                            render={record => {
                                const result = record?.result;
                                if (!result) return null;
                                return (
                                    <Box sx={{ 
                                        p: 2, 
                                        borderRadius: 1, 
                                        border: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                            Комментарий модератора:
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {result.commentFromModerator}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Модератор ID: {result.moderatorId}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />
                    )}
                    <FunctionField
                        label="История статусов"
                        render={record => {
                            const history = record?.statusHistory;
                            if (!history || history.length === 0) return 'История статусов недоступна';
                            return (
                                <Box sx={{ mt: 1 }}>
                                    {history.map((item: any, index: number) => (
                                        <Box key={index} sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 2, 
                                            mb: 1,
                                            p: 1,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <Chip 
                                                label={getStatusLabel(item.status)} 
                                                color={getStatusColor(item.status) as any}
                                                size="small"
                                            />
                                            <Typography variant="body2">
                                                {new Date(item.date).toLocaleString('ru-RU')}
                                            </Typography>
                                            {item.result && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.result.commentFromModerator}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        }}
                    />
                </SimpleShowLayout>
            </Show>
            
            {complaint?.currentComplaintStatus === 'NEW' && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => setTakeInWorkDialogOpen(true)}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Взять в работу
                    </Button>
                </Box>
            )}
            
            {complaint?.currentComplaintStatus === 'IN_PROGRESS' && (
                <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        size="large"
                        onClick={() => setRejectDialogOpen(true)}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Отказать в жалобе
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={() => setApproveDialogOpen(true)}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Одобрить жалобу
                    </Button>
                </Box>
            )}

            <Dialog
                open={takeInWorkDialogOpen}
                onClose={() => {
                    setTakeInWorkDialogOpen(false);
                    setYouTrackIssueUrl('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Взять жалобу в работу</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Перед тем как брать жалобу в работу, нужно завести задачу в YouTrack, в рамках которой будет вестись расследование.
                        </Typography>
                    </Alert>
                    <MuiTextField
                        autoFocus
                        margin="dense"
                        label="Ссылка на задачу YouTrack"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={youTrackIssueUrl}
                        onChange={(event) => setYouTrackIssueUrl(event.target.value)}
                        placeholder="https://youtrack.looton.ru/*"
                        error={youTrackIssueUrl.trim().length > 0 && !isValidYouTrackIssueUrl(youTrackIssueUrl)}
                        helperText={youTrackIssueUrl.trim().length === 0
                            ? 'Введите ссылку вида https://youtrack.looton.ru/*'
                            : (!isValidYouTrackIssueUrl(youTrackIssueUrl) ? 'Ссылка должна начинаться с https://youtrack.looton.ru/' : '')
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => window.open(YOU_TRACK_BOARD_URL, '_blank', 'noopener,noreferrer')}>
                        Перейти в YouTrack
                    </Button>
                    <Button onClick={() => {
                        setTakeInWorkDialogOpen(false);
                        setYouTrackIssueUrl('');
                    }}>
                        Отмена
                    </Button>
                    <Button
                        onClick={handleTakeInWork}
                        variant="contained"
                        disabled={!isValidYouTrackIssueUrl(youTrackIssueUrl)}
                    >
                        Взять в работу
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Диалог для отклонения жалобы */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Отклонить жалобу</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Комментарий будет показан пользователю!</strong>
                        </Typography>
                    </Alert>
                    <MuiTextField
                        autoFocus
                        margin="dense"
                        label="Комментарий"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Укажите причину отклонения жалобы"
                        error={comment.trim().length > 0 && (comment.trim().length < 30 || comment.trim().length > 1000)}
                        helperText={`${comment.trim().length}/1000 символов (минимум 30)`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setRejectDialogOpen(false);
                        setComment('');
                    }}>
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleReject} 
                        color="error" 
                        variant="contained"
                        disabled={!comment.trim() || comment.trim().length < 30 || comment.trim().length > 1000}
                    >
                        Отклонить
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Диалог для одобрения жалобы */}
            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Одобрить жалобу</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Комментарий будет показан пользователю!</strong>
                        </Typography>
                    </Alert>
                    <MuiTextField
                        autoFocus
                        margin="dense"
                        label="Комментарий"
                        type="text"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Укажите какая была проделана работа по жалобе"
                        error={comment.trim().length > 0 && (comment.trim().length < 30 || comment.trim().length > 1000)}
                        helperText={`${comment.trim().length}/1000 символов (минимум 30)`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setApproveDialogOpen(false);
                        setComment('');
                    }}>
                        Отмена
                    </Button>
                    <Button 
                        onClick={handleApprove} 
                        color="success" 
                        variant="contained"
                        disabled={!comment.trim() || comment.trim().length < 30 || comment.trim().length > 1000}
                    >
                        Одобрить
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
